import DOMPurify from 'dompurify';

/**
 * Sanitize HTML content to prevent Cross-Site Scripting (XSS) attacks.
 * Allows safe HTML formatting tags, email tables, styles, and inline attributes.
 */
export function sanitizeHtml(dirtyHtml) {
  if (!dirtyHtml) return '';
  if (typeof dirtyHtml !== 'string') return String(dirtyHtml);

  try {
    return DOMPurify.sanitize(dirtyHtml, {
      FORCE_BODY: false,
      ALLOWED_TAGS: [
        'p', 'br', 'strong', 'b', 'em', 'i', 'u', 's', 'strike', 'span', 'div',
        'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'ul', 'ol', 'li', 'blockquote', 'pre', 'code',
        'table', 'thead', 'tbody', 'tfoot', 'tr', 'th', 'td', 'colgroup', 'col', 'caption',
        'a', 'img', 'hr', 'font', 'center', 'style', 'section', 'article', 'header', 'footer'
      ],
      ALLOWED_ATTR: [
        'href', 'target', 'src', 'alt', 'title', 'class', 'style', 'width', 'height',
        'color', 'bgcolor', 'background', 'align', 'valign', 'border', 'cellpadding', 'cellspacing',
        'colspan', 'rowspan', 'nowrap', 'id', 'name', 'dir', 'role', 'data-filename'
      ],
      ALLOWED_URI_REGEXP: /^(?:(?:(?:f|ht)tps?|mailto|tel|data:image\/[a-z0-9+]+;base64,)|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i,
      ALLOW_DATA_ATTR: true,
      ADD_ATTR: ['target'],
      FORBID_TAGS: ['script', 'iframe', 'object', 'embed', 'form', 'input', 'button', 'link'],
      FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover', 'onfocus', 'onblur']
    });
  } catch (err) {
    console.warn('DOMPurify sanitize warning:', err);
    // Fallback: strip <script> tags
    return dirtyHtml.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '');
  }
}

/**
 * Strip all HTML tags and HTML entities to get clean plain text for email snippet preview & Kakao notifications.
 */
export function stripHtmlTags(htmlOrText) {
  if (!htmlOrText) return '';
  if (typeof htmlOrText !== 'string') return String(htmlOrText);

  return htmlOrText
    .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '')
    .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '')
    .replace(/<br\s*[\/]?>/gi, '\n')
    .replace(/<\/p>/gi, '\n')
    .replace(/<\/div>/gi, '\n')
    .replace(/<\/tr>/gi, '\n')
    .replace(/<\/li>/gi, '\n')
    .replace(/<li[^>]*>/gi, '• ')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&amp;/gi, '&')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/&apos;/gi, "'")
    .replace(/\r\n/g, '\n')
    .replace(/[ \t]+/g, ' ')
    .replace(/\n\s*\n\s*\n/g, '\n\n')
    .trim();
}

/**
 * Standard date & time formatter: YYYY.MM.DD HH:mm (KST)
 */
export function formatMailDateTime(dateVal) {
  if (!dateVal) return '';
  let str = String(dateVal).trim();
  // If ISO string without timezone (e.g. 2026-08-19T09:12:34), treat as UTC by appending Z
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?$/.test(str)) {
    str += 'Z';
  }
  const d = new Date(str);
  if (isNaN(d.getTime())) return String(dateVal);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const min = String(d.getMinutes()).padStart(2, '0');
  return `${yyyy}.${mm}.${dd} ${hh}:${min}`;
}

/**
 * Check if all recipient tokens belong to internal domain (@clouflake.com).
 * Robustly strips trailing commas, angle brackets, and whitespace.
 */
export function isInternalRecipient(recipientStr) {
  if (!recipientStr) return false;
  const tokens = String(recipientStr)
    .split(/[,;]/)
    .map(s => s.trim())
    .filter(Boolean);
  if (tokens.length === 0) return false;

  return tokens.every(token => {
    const angleMatch = token.match(/<([^>]+)>/);
    const email = (angleMatch ? angleMatch[1] : token).trim().toLowerCase();
    return email.endsWith('@clouflake.com') || email === 'all' || email === '전체';
  });
}
